import logo from '@/assets/images/Diagonal Arrow-01.png';
import favicon from '@/assets/images/favicon.ico';
import { AnalyticsWrapper } from '@/components/analytics-wrapper';
import { AppNavBar } from '@/components/app-navbar';
import { ShadcnThemeProvider } from '@/components/shadcn-theme-provider';
import { Loading } from '@/components/ui/loading';
import { Toaster } from '@/components/ui/toaster';
import { analyticsConfig } from '@/lib/analytics';
import { getLanguage, getLanguages } from '@/lib/cms';
import { getLocaleFromRequest } from '@/lib/getLocaleFromRequest';
import translateURL from '@/lib/translateUrl';
import styles from '@/styles/globals.css?url';
import { LanguageCode } from '@/types/language';
import { getMetaFromPathname } from '@/utils/metaTag';
import {
  AnalyticsBodyScripts,
  AnalyticsHeadScripts,
  AnalyticsProvider
} from '@growdash/analytics';
import type { Language } from '@growdash/cms-client/generated';
import { json, LoaderFunctionArgs } from '@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation
} from '@remix-run/react';
import { withSentry } from '@sentry/remix';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnv } from './lib/utils';

// Loader that provides language data from CMS on the server
export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const languages: Language[] = (await getLanguages()) ?? [];
  const locale = await getLocaleFromRequest(request);
  const translationData = await getLanguage(locale);
  // Retrieve translation data from CMS for the active language obtained from the URL.
  const cmsLanguageData = {
    language: locale,
    fallbackLng: LanguageCode.EN,
    supportedLngs: languages.map(lang => lang.key),
    defaultNS: 'common',
    translations: translationData?.translations
  };

  // Use the utility function to get meta data based on pathname
  const { title, description } = getMetaFromPathname(
    url.pathname,
    cmsLanguageData.translations || {}
  );

  return json({
    locale,
    domain: `${url.protocol}//${url.host}`,
    cmsLanguageData,
    languages,
    ENV: {
      VITE_CMS_BASE_PATH:
        import.meta.env.VITE_CMS_BASE_PATH || process.env.VITE_CMS_BASE_PATH,
      VITE_GROWDASH_DASHBOARD:
        import.meta.env.VITE_GROWDASH_DASHBOARD ||
        process.env.VITE_GROWDASH_DASHBOARD,
      VITE_SENTRY_DNS:
        import.meta.env.VITE_SENTRY_DNS || process.env.VITE_SENTRY_DNS,
      VITE_SENTRY_ORG:
        import.meta.env.VITE_SENTRY_ORG || process.env.VITE_SENTRY_ORG,
      VITE_SENTRY_AUTH_TOKEN:
        import.meta.env.VITE_SENTRY_AUTH_TOKEN ||
        process.env.VITE_SENTRY_AUTH_TOKEN,
      VITE_GOOGLE_TAG_KEY:
        import.meta.env.VITE_GOOGLE_TAG_KEY || process.env.VITE_GOOGLE_TAG_KEY,
      VITE_MIXPANEL_KEY:
        import.meta.env.VITE_MIXPANEL_KEY || process.env.VITE_MIXPANEL_KEY
    },
    title,
    description
  });
}

export const handle = {
  // i18n namespace configuration for the route
  i18n: 'common'
};

export const links = () => [
  {
    rel: 'stylesheet',
    href: styles
  },
  {
    rel: 'icon',
    href: favicon
  }
];

// Schema.org Organization
const organizationSchema = (domain: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Growdash',
  description,
  url: domain,
  logo: `${domain}${logo}`,
  foundingDate: '2022',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Dubai',
    addressCountry: 'AE'
  },
  sameAs: [
    'https://www.linkedin.com/company/growdash',
    'https://www.instagram.com/mygrowdash/',
    'https://www.facebook.com/mygrowdash'
  ]
});

function App() {
  const {
    locale,
    domain,
    cmsLanguageData,
    languages,
    ENV,
    title,
    description
  } = useLoaderData<typeof loader>();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [hydrated, setHydrated] = useState(false);

  // Hydration effect
  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') return;

    const storedLanguage = localStorage.getItem('i18nextLng');
    if (storedLanguage && location.pathname === '/') {
      i18n.changeLanguage(storedLanguage);
      navigate(translateURL(storedLanguage, '/'));
    }
  }, [i18n, navigate, location.pathname, hydrated]);

  // Always add the resource bundle if CMS translation data is available.
  useEffect(() => {
    if (!hydrated) return;

    if (cmsLanguageData?.translations) {
      i18next.addResourceBundle(
        locale,
        'common',
        cmsLanguageData.translations,
        false, // force update
        true
      );
      // Trigger language change only if the current language is different.
      if (i18n.language !== locale) {
        i18next.changeLanguage(locale);
      }
    }
  }, [locale, cmsLanguageData, i18n.language, hydrated]);

  const [queryClient] = useState(() => new QueryClient());
  const navigation = useNavigation();

  const analytic_config = {
    gtmId: getEnv('VITE_GOOGLE_TAG_KEY'),
    hubspotId: getEnv('VITE_HUBSPOT_ID'),
    cookieyesId: getEnv('VITE_COOKIEEYE_ID')
  };

  return (
    <html lang={i18n.language} dir={i18n.dir(i18n.language)}>
      <head>
        <title>{title}</title>
        <meta name='description' content={description} />
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <link rel='canonical' href={`${domain}${location.pathname}`} />

        {/* Social Media Meta Tags */}
        <meta property='og:title' content={title} />
        <meta property='og:description' content={description} />
        <meta property='og:site_name' content='Growdash' />
        <meta property='og:type' content='website' />
        <meta property='og:image' content={`${domain}${logo}`} />
        <meta property='og:url' content={`${domain}${location.pathname}`} />

        {/* Twitter Card Tags */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:site' content='@mygrowdash' />
        <meta name='twitter:creator' content='@mygrowdash' />
        <meta name='twitter:title' content={title} />
        <meta name='twitter:description' content={description} />
        <meta name='twitter:image' content={`${domain}${logo}`} />
        <meta
          name='twitter:image:alt'
          content='Growdash - Restaurant Business Intelligence Platform'
        />

        {/* Schema.org JSON-LD */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              organizationSchema(domain, t('organization.description'))
            )
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`
          }}
        />

        <Meta />
        <Links />

        <AnalyticsHeadScripts {...analytic_config} />
      </head>
      <body>
        <AnalyticsBodyScripts gtmId={analytic_config.gtmId} />
        {navigation.state === 'loading' && <Loading />}
        <ShadcnThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AnalyticsProvider config={analyticsConfig}>
              <AnalyticsWrapper>
                <Outlet />
                <AppNavBar languages={languages} />
                <Toaster />
              </AnalyticsWrapper>
            </AnalyticsProvider>
          </QueryClientProvider>
        </ShadcnThemeProvider>
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.__CMS_LANGUAGE_DATA__ = ${JSON.stringify(
              cmsLanguageData
            )}`
          }}
        />
        <Scripts />
        <LiveReload />
      </body>
    </html>
  );
}

export default withSentry(App);
